import { memo } from 'react';
import { useSelector } from 'react-redux';

import { ViewTypeName } from '@air/api/types';
import { useBreakpointsContext } from '@air/provider-media-query';
import { useShortIdContext } from '@air/provider-short-id';

import { usePublicPermissions } from '~/components/PublicPermissionsProvider';
import { ViewKanbanGroupByMenu } from '~/components/ViewControls/ViewKanbanGroupByMenu/ViewKanbanGroupByMenu';
import { ViewSortingMenu } from '~/components/ViewControls/ViewSortingMenu/ViewSortingMenu';
import { ViewTypeToggleMenu } from '~/components/ViewControls/ViewTypeToggleMenu/ViewTypeToggleMenu';
import { useShowExpandNestedInVisibilityMenu } from '~/components/ViewControls/ViewVisiblityToggleMenu/hooks/useShowExpandNestedInVisibilityMenu';
import { PublicViewVisibilityToggleMenu } from '~/components/ViewControls/ViewVisiblityToggleMenu/PublicViewVisibilityToggleMenu';
import { ViewVisibilityExpandNestedContentToggle } from '~/components/ViewControls/ViewVisiblityToggleMenu/ViewVisibilityExpandNestedContentToggle';
import { useClearSearchSortOverrideWhenQueryCleared } from '~/hooks/search/useClearSearchSortOverrideWhenQueryCleared';
import { currentViewTypeNameSelector } from '~/store/configViews/selectors';
import { isViewSortingMenuSupported } from '~/store/configViews/utils';
import { canSeeBoardCustomFields } from '~/utils/permissions/boardPermissions';

export const PublicBoardHeaderViewControls = memo(() => {
  useClearSearchSortOverrideWhenQueryCleared();
  const currentViewTypeName = useSelector(currentViewTypeNameSelector);
  const inKanbanView = currentViewTypeName === ViewTypeName.kanban;
  const isSortingMenuSupported = isViewSortingMenuSupported(currentViewTypeName);
  const { isAboveMediumScreen } = useBreakpointsContext();
  const { permissions } = usePublicPermissions();
  const canViewCustomFields = canSeeBoardCustomFields(permissions);
  const { presentationSettings } = useShortIdContext();
  const isExpandNestedDisabled = presentationSettings?.lockedControls?.expandNested === true;
  const { showExpandNestedInVisibilityMenu } = useShowExpandNestedInVisibilityMenu({
    isDisabled: isExpandNestedDisabled,
  });
  const isViewTypeLocked = presentationSettings?.lockedControls?.viewType === true;

  return (
    <div className="flex items-center gap-2">
      {inKanbanView && canViewCustomFields && <ViewKanbanGroupByMenu customFieldValues={undefined} />}

      {isAboveMediumScreen && isSortingMenuSupported && <ViewSortingMenu />}

      <PublicViewVisibilityToggleMenu
        extraItems={showExpandNestedInVisibilityMenu ? <ViewVisibilityExpandNestedContentToggle /> : undefined}
      />
      {!isViewTypeLocked && <ViewTypeToggleMenu canViewCustomFields={canViewCustomFields} />}
    </div>
  );
});

PublicBoardHeaderViewControls.displayName = 'PublicBoardHeaderViewControls';
